import { addMinutes, parseISO } from "date-fns";
import { TimeSlotPosition, DragData, DropZoneData } from "@/lib/schemas/calendarUtils";
import { type EnhancedBusinessHours } from "@/lib/businessHours";
import { getMinutesFromMidnightInTimeZone } from "@sylva/shared-types";

// Re-export types for convenience
export type { DragData, DropZoneData };

export const SLOT_HEIGHT = 40; // Default height for each time slot (≥15 min intervals)
export const SLOT_HEIGHT_SMALL = 20; // Height for small intervals (≤15 min)

// Helper function to get slot height based on interval
export const getSlotHeight = (intervalMinutes: number): number => {
  return intervalMinutes <= 15 ? SLOT_HEIGHT_SMALL : SLOT_HEIGHT;
};
export const DEFAULT_CALENDAR_START_HOUR = 8;
export const DEFAULT_CALENDAR_END_HOUR = 22;

// Legacy constants for backward compatibility
export const CALENDAR_START_HOUR = DEFAULT_CALENDAR_START_HOUR;
export const CALENDAR_END_HOUR = DEFAULT_CALENDAR_END_HOUR;

// Helper function to get the time interval from tenant settings
export const getTimeIntervalMinutes = (
  startTimeInterval?: "30min" | "auto" | "custom",
  customIntervalMinutes?: number
): number => {
  if (startTimeInterval === "custom") {
    return customIntervalMinutes || 30;
  }
  return 30; // Default to 30 minutes for "30min" and "auto"
};

// Helper function to get slots per hour based on interval
export const getSlotsPerHour = (intervalMinutes: number): number => {
  return 60 / intervalMinutes;
};

// Calculate dynamic calendar hours based on business hours
export const calculateDynamicCalendarHours = (businessHours?: EnhancedBusinessHours, intervalMinutes: number = 30) => {
  if (!businessHours) {
    return {
      startHour: DEFAULT_CALENDAR_START_HOUR,
      startMinute: 0,
      endHour: DEFAULT_CALENDAR_END_HOUR,
      endMinute: 0
    };
  }

  let earliestStartMinutes = 23 * 60; // Track in total minutes for precision
  let latestEndMinutes = 0;

  // Check all days to find the earliest start and latest end times
  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  daysOfWeek.forEach(day => {
    const dayHours = businessHours[day as keyof EnhancedBusinessHours];

    if (dayHours?.available && dayHours.timeRanges?.length > 0) {
      dayHours.timeRanges.forEach(range => {
        const startHour = parseInt(range.start.split(':')[0]);
        const startMinute = parseInt(range.start.split(':')[1]);
        const endHour = parseInt(range.end.split(':')[0]);
        const endMinute = parseInt(range.end.split(':')[1]);

        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        if (startTotalMinutes < earliestStartMinutes) {
          earliestStartMinutes = startTotalMinutes;
        }

        if (endTotalMinutes > latestEndMinutes) {
          latestEndMinutes = endTotalMinutes;
        }
      });
    }
  });

  // If no business hours found, use defaults
  if (earliestStartMinutes === 23 * 60 && latestEndMinutes === 0) {
    return {
      startHour: DEFAULT_CALENDAR_START_HOUR,
      startMinute: 0,
      endHour: DEFAULT_CALENDAR_END_HOUR,
      endMinute: 0
    };
  }

  // Apply one interval buffer before and after business hours
  const calendarStartMinutes = Math.max(0, earliestStartMinutes - intervalMinutes);
  const calendarEndMinutes = Math.min(24 * 60, latestEndMinutes + intervalMinutes);

  return {
    startHour: Math.floor(calendarStartMinutes / 60),
    startMinute: calendarStartMinutes % 60,
    endHour: Math.floor(calendarEndMinutes / 60),
    endMinute: calendarEndMinutes % 60
  };
};

export const getAppointmentPosition = (
  startTime: string,
  endTime: string,
  intervalMinutes: number = 30,
  calendarStartHour: number = CALENDAR_START_HOUR,
  calendarEndHour: number = CALENDAR_END_HOUR,
  calendarStartMinute: number = 0,
  calendarEndMinute: number = 0,
  timeZone?: string
): TimeSlotPosition => {
  const startTimeInMinutes = timeZone
    ? getMinutesFromMidnightInTimeZone(startTime, timeZone)
    : (() => {
        const start = parseISO(startTime);
        return (start.getHours() * 60) + start.getMinutes();
      })();
  const endTimeInMinutes = timeZone
    ? getMinutesFromMidnightInTimeZone(endTime, timeZone)
    : (() => {
        const end = parseISO(endTime);
        return (end.getHours() * 60) + end.getMinutes();
      })();
  const calendarStartInMinutes = calendarStartHour * 60 + calendarStartMinute;
  const calendarEndInMinutes = calendarEndHour * 60 + calendarEndMinute;

  const slotHeight = getSlotHeight(intervalMinutes);

  // Clip both start and end times to the calendar's visible hours
  const visibleStartInMinutes = Math.max(startTimeInMinutes, calendarStartInMinutes);
  const visibleEndInMinutes = Math.min(endTimeInMinutes, calendarEndInMinutes);

  // Calculate position and height based on visible portion
  const top = ((visibleStartInMinutes - calendarStartInMinutes) / intervalMinutes) * slotHeight;
  const visibleDurationInMinutes = Math.max(0, visibleEndInMinutes - visibleStartInMinutes);
  const fullHeight = (visibleDurationInMinutes / intervalMinutes) * slotHeight;
  const height = Math.max(10, fullHeight - 2); // Reduce by fixed 2px for visual separation, minimum 10px

  return {
    top: Math.max(0, top),
    height: height,
  };
};

export const getBufferPosition = (
  startTime: string, 
  endTime: string, 
  bufferMinutes: number, 
  isBeforeBuffer: boolean, 
  intervalMinutes: number = 30,
  calendarStartHour: number = CALENDAR_START_HOUR,
  timeZone?: string
): TimeSlotPosition => {
  const appointmentStart = parseISO(startTime);
  const appointmentEnd = parseISO(endTime);
  
  // Calculate the buffer start position
  let bufferStartTime: Date;
  if (isBeforeBuffer) {
    // Before buffer: starts X minutes before appointment start
    bufferStartTime = addMinutes(appointmentStart, -bufferMinutes);
  } else {
    // After buffer: starts at appointment end
    bufferStartTime = appointmentEnd;
  }
  
  const bufferTimeInMinutes = timeZone
    ? getMinutesFromMidnightInTimeZone(bufferStartTime, timeZone)
    : (bufferStartTime.getHours() * 60) + bufferStartTime.getMinutes();
  
  const slotHeight = getSlotHeight(intervalMinutes);
  const startOffset = ((bufferTimeInMinutes - (calendarStartHour * 60)) / intervalMinutes) * slotHeight;
  const fullHeight = (bufferMinutes / intervalMinutes) * slotHeight;
  const height = Math.max(5, fullHeight - 2); // Reduce by fixed 2px for visual separation, minimum 5px
  
  return {
    top: Math.max(0, startOffset),
    height: height,
  };
};

export const calculateNewTimeFromPosition = (
  date: Date,
  yPosition: number,
  duration: number,
  intervalMinutes: number = 30,
  calendarStartHour: number = CALENDAR_START_HOUR
): { startTime: string; endTime: string } => {
  // Calculate which time slot based on Y position
  const slotHeight = getSlotHeight(intervalMinutes);
  const slotIndex = Math.floor(yPosition / slotHeight);
  const totalMinutesFromStart = slotIndex * intervalMinutes;
  
  // Create start time
  const startTime = new Date(date);
  startTime.setHours(calendarStartHour, 0, 0, 0);
  const newStartTime = addMinutes(startTime, totalMinutesFromStart);
  
  // Calculate end time based on duration
  const newEndTime = addMinutes(newStartTime, duration);
  
  return {
    startTime: newStartTime.toISOString(),
    endTime: newEndTime.toISOString(),
  };
};

export const getTimeSlotFromPosition = (yPosition: number, intervalMinutes: number = 30, calendarStartHour: number = CALENDAR_START_HOUR): string => {
  const slotHeight = getSlotHeight(intervalMinutes);
  const slotIndex = Math.floor(yPosition / slotHeight);
  const totalMinutesFromStart = slotIndex * intervalMinutes;
  const hours = Math.floor(totalMinutesFromStart / 60) + calendarStartHour;
  const minutes = totalMinutesFromStart % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const isValidTimeSlot = (timeSlot: string, calendarStartHour: number = CALENDAR_START_HOUR, calendarEndHour: number = CALENDAR_END_HOUR): boolean => {
  const [hours] = timeSlot.split(':').map(Number);
  return hours >= calendarStartHour && hours <= calendarEndHour;
};

export const parseTimeSlot = (timeSlot: string, date: Date): Date => {
  const [hours, minutes] = timeSlot.split(':').map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const checkAppointmentOverlap = (
  newStart: string,
  newEnd: string,
  existingAppointments: Array<{ start_time: string; end_time: string; id: string }>,
  excludeId?: string
): boolean => {
  const newStartTime = parseISO(newStart);
  const newEndTime = parseISO(newEnd);
  
  return existingAppointments.some(appointment => {
    if (excludeId && appointment.id === excludeId) return false;
    
    const existingStart = parseISO(appointment.start_time);
    const existingEnd = parseISO(appointment.end_time);
    
    // Check for overlap: new appointment starts before existing ends and ends after existing starts
    return newStartTime < existingEnd && newEndTime > existingStart;
  });
};
